import { useEffect, useState } from 'react';
import { ReuseSettings, SourceBanner, StartFrom } from '@/components/reuse';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Check, Copy, Image, Plus, Trash2, X } from 'lucide-react';
import {
  AUDIENCE_BADGES,
  type AudienceScope,
  type ContentSource,
  type QuizOverlayView,
  type ReusePolicy,
  LEVELS,
  LEVEL_LABELS,
  QUESTION_TYPES,
  SKILLS,
  skillLabel,
  suggestTopics,
  type Level,
  type Topic,
  type QuestionInput,
  type QuestionOption,
  type QuestionType,
  type QuizAudience,
  type QuizMode,
} from '@ready/shared';
import { AudiencePicker } from '@/components/audience-picker';
import { useApi, type ContentOwner, type Quiz, type QuizQuestionRef } from '@/lib/api';
import { Badge, Button, Card, ErrorText, Field, Input, PageHeader, Select, Textarea } from '../ui';
import { AiButton, useAiEnabled } from '@/components/ai/ai-assist';
import { QuestionAssist } from '@/components/ai/question-assist';
import { SearchBox, TopicPicker } from '@/components/catalog';
import { useDebounced } from '@/lib/use-debounced';

/*
 * Quiz and question authoring, shared by an org's Quizzes tab
 * (`/organizations/:orgId/...`) and Nirvik's Library (`/admin/library/...`).
 * `base` is the route prefix for the owner's quiz pages.
 */

const ownerKey = (o: ContentOwner) => (o.kind === 'org' ? o.orgId : 'library');
const STATUS: Record<Quiz['status'], string> = {
  draft: 'Draft',
  published: 'Live',
  archived: 'Archived',
};
const TYPE_LABELS: Record<QuestionType, string> = {
  single: 'One answer',
  multiple: 'Several answers',
  true_false: 'True or false',
};

export function CopyLinkButton({ path, label = 'Copy link' }: { path: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="secondary"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(`${window.location.origin}${path}`);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // Clipboard blocked; nothing else to do.
        }
      }}
    >
      {copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
      {copied ? 'Copied' : label}
    </Button>
  );
}

/** Opens the printable and shareable posters for a published course, quiz or event. */
export function PostersButton({ path }: { path: string }) {
  return (
    <Link to={`${path}/poster`}>
      <Button variant="secondary">
        <Image className="size-4" aria-hidden /> Posters
      </Button>
    </Link>
  );
}

// ─── Quiz list ─────────────────────────────────────────────────────────

export function QuizList({ owner, base }: { owner: ContentOwner; base: string }) {
  const api = useApi();
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const quizzes = useQuery({
    queryKey: ['quizzes', ownerKey(owner), q],
    queryFn: () => api.quizzes(owner, { q }),
    placeholderData: (prev) => prev,
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={`${base}/question-banks`}>
          <Button variant="secondary">Question banks</Button>
        </Link>
        <Link to={`${base}/quizzes/new`}>
          <Button>
            <Plus className="size-4" aria-hidden /> New quiz
          </Button>
        </Link>
      </div>
      <SearchBox value={text} onChange={setText} placeholder="Search quizzes by title" />
      <ErrorText error={quizzes.error} />
      {quizzes.data && <ListCount shown={quizzes.data.quizzes.length} total={quizzes.data.total} />}
      {quizzes.data?.quizzes.length === 0 && (
        <Card>
          <p className="text-sm text-muted-foreground">
            {q
              ? 'No quizzes match that title.'
              : 'No quizzes yet. Create one, then add questions from your question banks.'}
          </p>
        </Card>
      )}
      {quizzes.data?.quizzes.map((q) => (
        <Card key={q.id} className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to={`/quizzes/${q.id}/edit`} className="font-medium hover:text-primary">
              {q.title}
            </Link>
            <div className="mt-1 flex flex-wrap gap-2">
              <Badge>{STATUS[q.status]}</Badge>
              <Badge>{AUDIENCE_LABELS[q.audience]}</Badge>
              <Badge>{q.mode === 'self_assessment' ? 'Self-assessment' : 'Assessment'}</Badge>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {q.status === 'published' && q.audience !== 'course' && (
              <>
                <CopyLinkButton path={`/q/${q.slug}`} />
                <PostersButton path={`/q/${q.slug}`} />
              </>
            )}
            <Link to={`/quizzes/${q.id}/edit`}>
              <Button variant="ghost">Edit</Button>
            </Link>
          </div>
        </Card>
      ))}
    </div>
  );
}

/** "Showing 200 of 1,240. Search to find the rest." when a list is capped. */
export function ListCount({ shown, total }: { shown: number; total: number }) {
  if (total <= shown) return null;
  return (
    <p className="text-sm text-muted-foreground">
      Showing the {shown.toLocaleString()} most recently updated of {total.toLocaleString()}. Search
      to find the rest.
    </p>
  );
}

const AUDIENCE_LABELS: Record<QuizAudience, string> = {
  ...AUDIENCE_BADGES,
  course: 'Course assessment',
};

// ─── New quiz (rare: its own page) ─────────────────────────────────────

export function NewQuizForm({
  owner,
  back,
}: {
  owner: ContentOwner;
  back: { to: string; label: string };
}) {
  const api = useApi();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [mode, setMode] = useState<QuizMode>('self_assessment');
  const [audience, setAudience] = useState<QuizAudience>(
    owner.kind === 'library' ? 'public' : 'org',
  );
  const [scope, setScope] = useState<AudienceScope | null>(null);
  const create = useMutation({
    mutationFn: () => api.createQuiz(owner, { title, mode, audience, audienceScope: scope }),
    onSuccess: (quiz) => navigate(`/quizzes/${quiz.id}/edit`),
  });
  return (
    <div className="max-w-3xl">
      <PageHeader
        title="New quiz"
        description="Name it and choose how it runs. You'll add questions next."
        back={back}
      />
      <Card>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Field label="Title" htmlFor="quiz-title">
            <Input
              id="quiz-title"
              required
              minLength={3}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Phishing awareness check"
            />
          </Field>
          <ModeAndAudience
            owner={owner}
            mode={mode}
            setMode={setMode}
            audience={audience}
            scope={scope}
            setAudience={(a, s) => {
              setAudience(a);
              setScope(s);
            }}
          />
          <Button type="submit" disabled={create.isPending}>
            Create and add questions
          </Button>
          <ErrorText error={create.error} />
        </form>
      </Card>
      <div className="mt-6">
        <StartFrom kind="quiz" owner={owner} />
      </div>
    </div>
  );
}

function ModeAndAudience({
  owner,
  mode,
  setMode,
  audience,
  scope,
  setAudience,
}: {
  owner: ContentOwner;
  mode: QuizMode;
  setMode: (m: QuizMode) => void;
  audience: QuizAudience;
  scope: AudienceScope | null;
  setAudience: (a: QuizAudience, scope: AudienceScope | null) => void;
}) {
  return (
    <div className="space-y-4">
      <Field
        label="Type"
        htmlFor="quiz-mode"
        hint={mode === 'self_assessment' ? 'Feedback after each answer' : 'Results only at the end'}
      >
        <Select
          id="quiz-mode"
          className="sm:max-w-xs"
          value={mode}
          onChange={(e) => setMode(e.target.value as QuizMode)}
        >
          <option value="self_assessment">Self-assessment</option>
          <option value="assessment">Assessment</option>
        </Select>
      </Field>
      <AudiencePicker
        owner={owner}
        id="quiz-audience"
        label="Who can take it"
        audience={audience}
        scope={scope}
        onChange={(a, s) => setAudience(a as QuizAudience, s)}
        extraOptions={[['course', 'Only inside a course (final assessment)']]}
        extraHint="A course's final assessment: hidden everywhere else, and open to learners only after they finish the course's required lessons."
      />
    </div>
  );
}

/**
 * A customized quiz's share of its source: every source question, with the
 * ones it leaves out. New source versions flow in automatically.
 */
function SourceQuestions({
  source,
  overlay,
  hidden,
  onChange,
}: {
  source: ContentSource;
  overlay: QuizOverlayView;
  hidden: string[];
  onChange: (hidden: string[]) => void;
}) {
  const included = overlay.questions.length - hidden.length;
  return (
    <Card title={`From ${source.title} (${included} of ${overlay.questions.length})`}>
      <p className="mb-3 text-sm text-muted-foreground">
        These follow {source.ownerName}&rsquo;s quiz: when it publishes a new version, yours is
        updated automatically with your changes kept. Uncheck any you want to leave out.
      </p>
      {overlay.goneFromSource > 0 && (
        <p className="mb-3 text-sm text-primary-strong">
          {overlay.goneFromSource === 1
            ? 'One question you left out is no longer in the source.'
            : `${overlay.goneFromSource} questions you left out are no longer in the source.`}
        </p>
      )}
      <ol className="space-y-1">
        {overlay.questions.map((q, i) => (
          <li key={q.id}>
            <label className="flex items-start gap-2 rounded-lg p-2 text-sm hover:bg-muted/50">
              <input
                type="checkbox"
                className="mt-0.5 size-5 shrink-0 accent-primary"
                checked={!hidden.includes(q.id)}
                onChange={(e) =>
                  onChange(
                    e.target.checked ? hidden.filter((id) => id !== q.id) : [...hidden, q.id],
                  )
                }
              />
              <span className="w-5 shrink-0 tabular-nums text-muted-foreground">{i + 1}.</span>
              <span className={hidden.includes(q.id) ? 'text-muted-foreground line-through' : ''}>
                {q.prompt}
              </span>
            </label>
          </li>
        ))}
      </ol>
    </Card>
  );
}

// ─── Quiz editor ───────────────────────────────────────────────────────

export function QuizEditorPage() {
  const { quizId = '' } = useParams();
  const api = useApi();
  const quiz = useQuery({ queryKey: ['quiz', quizId], queryFn: () => api.quiz(quizId) });
  if (quiz.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (quiz.isError) return <ErrorText error={quiz.error} />;
  return <QuizEditor key={quiz.data.id} quiz={quiz.data} />;
}

function QuizEditor({
  quiz,
}: {
  quiz: Quiz & {
    questions: QuizQuestionRef[];
    source: ContentSource | null;
    overlay: QuizOverlayView | null;
  };
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const owner: ContentOwner = quiz.ownerOrgId
    ? { kind: 'org', orgId: quiz.ownerOrgId }
    : { kind: 'library' };
  const base = quiz.ownerOrgId ? `/organizations/${quiz.ownerOrgId}/library` : '/admin/library';

  const [title, setTitle] = useState(quiz.title);
  const [description, setDescription] = useState(quiz.description ?? '');
  const [mode, setMode] = useState<QuizMode>(quiz.mode);
  const [audience, setAudience] = useState(quiz.audience);
  const [scope, setScope] = useState<AudienceScope | null>(quiz.audienceScope ?? null);
  const [reuse, setReuse] = useState<ReusePolicy | null>(quiz.reuse ?? null);
  // A customized quiz: source questions left out (its own added ones are `selected`).
  const [hidden, setHidden] = useState<string[]>(quiz.overlay?.hidden ?? []);
  const fromSource = quiz.overlay
    ? quiz.overlay.questions.filter((q) => !hidden.includes(q.id)).length
    : 0;
  const [passPercent, setPassPercent] = useState(String(quiz.settings.passPercent));
  const [certificate, setCertificate] = useState(quiz.settings.certificate);
  const [shuffle, setShuffle] = useState(quiz.settings.shuffleQuestions);
  const [maxAttempts, setMaxAttempts] = useState(quiz.settings.maxAttempts?.toString() ?? '');
  const [level, setLevel] = useState<Level>(quiz.level);
  const [topics, setTopics] = useState<Topic[]>(quiz.topics);
  const [tags, setTags] = useState(quiz.tags.join(', '));
  // Selected questions in order, with enough detail to show them whichever bank they came from.
  const [selected, setSelected] = useState<QuizQuestionRef[]>(quiz.questions);
  const total = fromSource + selected.length;
  const aiEnabled = useAiEnabled();
  const [assist, setAssist] = useState(false);

  const input = () => ({
    title,
    description: description || undefined,
    mode,
    audience,
    audienceScope: scope,
    reuse,
    settings: {
      passPercent: Number(passPercent),
      certificate,
      shuffleQuestions: shuffle,
      maxAttempts: maxAttempts ? Number(maxAttempts) : null,
    },
    level,
    topics,
    tags: tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 20),
    questionIds: selected.map((q) => q.id),
    ...(quiz.overlay ? { hiddenQuestionIds: hidden } : {}),
  });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['quiz', quiz.id] });
    queryClient.invalidateQueries({ queryKey: ['quizzes'] });
  };
  const save = useMutation({
    mutationFn: () => api.updateQuiz(quiz.id, input()),
    onSuccess: invalidate,
  });
  const publish = useMutation({
    mutationFn: async () => {
      await api.updateQuiz(quiz.id, input());
      return api.publishQuiz(quiz.id);
    },
    onSuccess: invalidate,
  });
  const archive = useMutation({
    mutationFn: () => api.archiveQuiz(quiz.id, quiz.status !== 'archived'),
    onSuccess: invalidate,
  });

  const move = (i: number, d: -1 | 1) =>
    setSelected((s) => {
      const a = [...s];
      [a[i], a[i + d]] = [a[i + d]!, a[i]!];
      return a;
    });

  return (
    <div className="space-y-6">
      <PageHeader
        title={quiz.title}
        description={
          quiz.status === 'published'
            ? 'Live. Publishing again releases your changes as a new version; finished attempts keep the version they took.'
            : 'Draft. Publish when it has at least 3 questions.'
        }
        back={{ to: `${base}/quizzes`, label: 'Quizzes' }}
        action={
          <div className="flex flex-wrap gap-2">
            {quiz.status === 'published' && quiz.audience !== 'course' && (
              <CopyLinkButton path={`/q/${quiz.slug}`} label="Copy share link" />
            )}
            <Button variant="secondary" disabled={save.isPending} onClick={() => save.mutate()}>
              Save draft
            </Button>
            <Button disabled={publish.isPending || total < 3} onClick={() => publish.mutate()}>
              {quiz.status === 'published' ? 'Publish changes' : 'Publish'}
            </Button>
          </div>
        }
      />
      <SourceBanner kind="quiz" source={quiz.source} customized={Boolean(quiz.overlay)} />
      <ErrorText error={save.error ?? publish.error ?? archive.error} />
      {publish.isSuccess && <p className="text-sm text-india-green">Published.</p>}

      <Card title="Details">
        <div className="space-y-4">
          <Field label="Title" htmlFor="ed-title">
            <Input id="ed-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Description" htmlFor="ed-desc" hint="Shown before someone starts">
            <Textarea
              id="ed-desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <ModeAndAudience
            owner={owner}
            mode={mode}
            setMode={setMode}
            audience={audience}
            scope={scope}
            setAudience={(a, s) => {
              setAudience(a);
              setScope(s);
            }}
          />
          {audience !== 'course' && (
            <ReuseSettings kind="quiz" owner={owner} value={reuse} onChange={setReuse} />
          )}
          <div className="flex flex-wrap items-end gap-6">
            <Field label="Pass mark (%)" htmlFor="ed-pass">
              <Input
                id="ed-pass"
                type="number"
                min={0}
                max={100}
                className="w-24"
                value={passPercent}
                onChange={(e) => setPassPercent(e.target.value)}
              />
            </Field>
            <Field
              label="Max attempts"
              htmlFor="ed-attempts"
              hint="Per person. Limits guessing through retakes."
            >
              <Input
                id="ed-attempts"
                type="number"
                min={1}
                max={100}
                className="w-28"
                placeholder="Unlimited"
                value={maxAttempts}
                onChange={(e) => setMaxAttempts(e.target.value)}
              />
            </Field>
            {/* Course assessments complete the course, which issues the certificate. */}
            {audience !== 'course' && (
              <label className="flex items-center gap-2 pb-2 text-sm">
                <input
                  type="checkbox"
                  className="size-5 accent-primary"
                  checked={certificate}
                  onChange={(e) => setCertificate(e.target.checked)}
                />{' '}
                Certificate for a pass
              </label>
            )}
            <label className="flex items-center gap-2 pb-2 text-sm">
              <input
                type="checkbox"
                className="size-5 accent-primary"
                checked={shuffle}
                onChange={(e) => setShuffle(e.target.checked)}
              />{' '}
              Shuffle questions
            </label>
          </div>
          {/* Course assessments are never listed, so they need no catalog details. */}
          {audience !== 'course' && (
            <div className="space-y-4 border-t border-border pt-4">
              <div>
                <p className="font-medium">How learners find it</p>
                <p className="text-sm text-muted-foreground">
                  Used by search and filters in Explore. Changes reach learners when you publish.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Level" htmlFor="ed-level">
                  <Select
                    id="ed-level"
                    value={level}
                    onChange={(e) => setLevel(e.target.value as Level)}
                  >
                    {LEVELS.map((l) => (
                      <option key={l} value={l}>
                        {LEVEL_LABELS[l]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field
                  label="Search tags"
                  htmlFor="ed-tags"
                  hint="Comma-separated words people might search for."
                >
                  <Input id="ed-tags" value={tags} onChange={(e) => setTags(e.target.value)} />
                </Field>
              </div>
              <TopicPicker
                value={topics}
                onChange={setTopics}
                suggested={suggestTopics(selected.map((q) => q.skill))}
              />
            </div>
          )}
        </div>
      </Card>

      {quiz.overlay && quiz.source && (
        <SourceQuestions
          source={quiz.source}
          overlay={quiz.overlay}
          hidden={hidden}
          onChange={setHidden}
        />
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card
          title={
            quiz.overlay
              ? `Your added questions (${selected.length})`
              : `In this quiz (${selected.length})`
          }
        >
          {selected.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {quiz.overlay
                ? 'Optional: add questions of your own after the source’s.'
                : 'Pick a question bank and add questions.'}
            </p>
          )}
          <ol className="space-y-2">
            {selected.map((q, i) => (
              <li
                key={q.id}
                className="flex flex-col gap-1 rounded-lg border border-border p-2 text-sm sm:flex-row sm:items-start"
              >
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <span className="w-5 shrink-0 tabular-nums text-muted-foreground">{i + 1}.</span>
                  <span className="flex-1">
                    {q.prompt}
                    <span className="block text-xs text-muted-foreground">
                      {skillLabel(q.skill)} · {q.bankTitle}
                    </span>
                  </span>
                </div>
                {/* Phones: controls on their own row so the question keeps the full width. */}
                <div className="flex shrink-0 justify-end">
                  <button
                    type="button"
                    aria-label="Move up"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-30"
                  >
                    <ArrowUp className="size-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Move down"
                    disabled={i === selected.length - 1}
                    onClick={() => move(i, 1)}
                    className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-30"
                  >
                    <ArrowDown className="size-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Remove"
                    onClick={() => setSelected((s) => s.filter((x) => x.id !== q.id))}
                    className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-30"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              </li>
            ))}
          </ol>
        </Card>

        <div className="space-y-4">
          {assist ? (
            <QuestionAssist
              owner={owner}
              title="Draft new questions with AI"
              defaultBankTitle={`${title} questions`}
              defaultCount={10}
              saveLabel="Save and add"
              onSaved={(qs, bank) =>
                setSelected((s) => [
                  ...s,
                  ...qs.map((q) => ({
                    id: q.id,
                    prompt: q.prompt,
                    skill: q.skill,
                    bankId: bank.id,
                    bankTitle: bank.title,
                  })),
                ])
              }
              onClose={() => setAssist(false)}
            />
          ) : (
            aiEnabled && (
              <AiButton type="button" onClick={() => setAssist(true)}>
                Draft new questions with AI
              </AiButton>
            )
          )}

          <QuestionPicker
            owner={owner}
            base={base}
            returnTo={`/quizzes/${quiz.id}/edit`}
            selectedIds={new Set(selected.map((q) => q.id))}
            onAdd={(q) => setSelected((s) => [...s, q])}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="ghost" disabled={archive.isPending} onClick={() => archive.mutate()}>
          {quiz.status === 'archived' ? 'Restore quiz' : 'Archive quiz'}
        </Button>
      </div>
    </div>
  );
}

/** Choose a question bank, then search and add its questions. */
function QuestionPicker({
  owner,
  base,
  returnTo,
  selectedIds,
  onAdd,
}: {
  owner: ContentOwner;
  base: string;
  returnTo: string;
  selectedIds: Set<string>;
  onAdd: (q: QuizQuestionRef) => void;
}) {
  const api = useApi();
  const banks = useQuery({ queryKey: ['banks', ownerKey(owner)], queryFn: () => api.banks(owner) });
  const [bankId, setBankId] = useState('');
  const [search, setSearch] = useState('');
  const term = useDebounced(search);
  const activeBank = banks.data?.banks.find((b) => b.id === bankId) ?? banks.data?.banks[0];
  const questions = useQuery({
    queryKey: ['bank', activeBank?.id, term],
    queryFn: () => api.bank(activeBank!.id, term),
    enabled: Boolean(activeBank),
  });
  const available = (questions.data?.questions ?? []).filter((q) => !selectedIds.has(q.id));

  return (
    <Card
      title="Add questions"
      action={
        activeBank?.editable && (
          <Link
            to={`${base}/question-banks/${activeBank.id}/questions/new?returnTo=${encodeURIComponent(returnTo)}`}
            className="-my-2 py-2 text-sm text-primary"
          >
            New question
          </Link>
        )
      }
    >
      {banks.data?.banks.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No question banks yet.{' '}
          <Link to={`${base}/question-banks/new`} className="text-primary">
            Create one
          </Link>{' '}
          first.
        </p>
      ) : (
        <>
          <div className="mb-3 grid gap-3 sm:grid-cols-2">
            <Field label="Question bank" htmlFor="pick-bank">
              <Select
                id="pick-bank"
                value={activeBank?.id ?? ''}
                onChange={(e) => setBankId(e.target.value)}
              >
                {banks.data?.banks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.title} ({b.questionCount}){b.editable ? '' : ' · Nirvik library'}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Search" htmlFor="pick-search">
              <Input
                id="pick-search"
                type="search"
                placeholder="Keyword"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Field>
          </div>
          {activeBank?.description && (
            <p className="mb-3 text-xs text-muted-foreground">{activeBank.description}</p>
          )}
          <ul className="max-h-[28rem] space-y-2 overflow-y-auto">
            {available.map((q) => (
              <li
                key={q.id}
                className="flex items-start gap-2 rounded-lg border border-border p-2 text-sm"
              >
                <span className="flex-1">
                  {q.prompt}
                  <span className="block text-xs text-muted-foreground">{skillLabel(q.skill)}</span>
                </span>
                <button
                  type="button"
                  aria-label="Add to quiz"
                  onClick={() =>
                    onAdd({
                      id: q.id,
                      prompt: q.prompt,
                      skill: q.skill,
                      bankId: q.bankId,
                      bankTitle: activeBank!.title,
                    })
                  }
                  className="text-primary"
                >
                  <Plus className="size-4" />
                </button>
              </li>
            ))}
            {questions.data && available.length === 0 && (
              <li className="text-sm text-muted-foreground">
                {term ? `No questions match “${term}”.` : 'Nothing else to add from this bank.'}
              </li>
            )}
          </ul>
        </>
      )}
    </Card>
  );
}

// ─── Question banks ────────────────────────────────────────────────────

export function QuestionBanks({ owner, base }: { owner: ContentOwner; base: string }) {
  const api = useApi();
  const banks = useQuery({ queryKey: ['banks', ownerKey(owner)], queryFn: () => api.banks(owner) });
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Link to={`${base}/question-banks/new`}>
          <Button>
            <Plus className="size-4" aria-hidden /> New question bank
          </Button>
        </Link>
      </div>
      <ErrorText error={banks.error} />
      {banks.data?.banks.length === 0 && (
        <Card>
          <p className="text-sm text-muted-foreground">
            No question banks yet. Create one per topic, technology or sector.
          </p>
        </Card>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {banks.data?.banks.map((b) => (
          <Link key={b.id} to={`${base}/question-banks/${b.id}`}>
            <Card className="h-full hover:border-primary">
              <div className="flex items-start justify-between gap-3">
                <p className="font-medium">{b.title}</p>
                {!b.editable && <Badge>Nirvik library</Badge>}
              </div>
              {b.description && (
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{b.description}</p>
              )}
              <p className="mt-3 text-sm tabular-nums text-muted-foreground">
                {b.questionCount} question{b.questionCount === 1 ? '' : 's'}
              </p>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

/** Create or edit a bank's title and description (rare: its own page). */
export function QuestionBankForm({ owner, base }: { owner: ContentOwner; base: string }) {
  const { bankId } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const existing = useQuery({
    queryKey: ['bank', bankId, ''],
    queryFn: () => api.bank(bankId!),
    enabled: Boolean(bankId),
  });
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  useEffect(() => {
    if (existing.data) {
      setTitle(existing.data.bank.title);
      setDescription(existing.data.bank.description ?? '');
    }
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () => {
      const input = { title, description: description || undefined };
      return bankId ? api.updateBank(bankId, input) : api.createBank(owner, input);
    },
    onSuccess: (bank) => {
      queryClient.invalidateQueries({ queryKey: ['banks'] });
      queryClient.invalidateQueries({ queryKey: ['bank', bank.id] });
      navigate(`${base}/question-banks/${bank.id}`);
    },
  });

  return (
    <div className="max-w-3xl">
      <PageHeader
        title={bankId ? 'Edit question bank' : 'New question bank'}
        description={
          bankId
            ? undefined
            : 'A bank groups questions by topic, technology or sector, such as “Cloud security” or “Healthcare privacy”.'
        }
        back={{
          to: bankId ? `${base}/question-banks/${bankId}` : `${base}/question-banks`,
          label: bankId ? 'Back to bank' : 'Question banks',
        }}
      />
      <Card>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="Title" htmlFor="bank-title">
            <Input
              id="bank-title"
              required
              minLength={3}
              maxLength={120}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field
            label="Description"
            htmlFor="bank-desc"
            hint="What's in this bank and who it's for"
          >
            <Textarea
              id="bank-desc"
              rows={3}
              maxLength={1000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={save.isPending}>
            {bankId ? 'Save' : 'Create question bank'}
          </Button>
          <ErrorText error={save.error} />
        </form>
      </Card>
    </div>
  );
}

/** One bank: search its questions, add and edit them. */
export function QuestionBankDetail({ owner, base }: { owner: ContentOwner; base: string }) {
  const { bankId = '' } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const aiEnabled = useAiEnabled();
  const [assist, setAssist] = useState(false);
  const term = useDebounced(search);
  const data = useQuery({
    queryKey: ['bank', bankId, term],
    queryFn: () => api.bank(bankId, term),
  });
  const editable = data.data
    ? owner.kind === 'org'
      ? data.data.bank.ownerOrgId === owner.orgId
      : data.data.bank.ownerOrgId === null
    : false;
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['bank', bankId] });
    queryClient.invalidateQueries({ queryKey: ['banks'] });
  };
  const archiveQuestion = useMutation({
    mutationFn: (id: string) => api.archiveQuestion(id),
    onSuccess: refresh,
  });
  const archiveBank = useMutation({
    mutationFn: () => api.archiveBank(bankId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banks'] });
      navigate(`${base}/question-banks`);
    },
  });

  if (data.isPending && !term) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (data.isError) return <ErrorText error={data.error} />;
  const bank = data.data?.bank;

  return (
    <div className="space-y-4">
      <PageHeader
        title={bank?.title ?? ''}
        description={bank?.description ?? undefined}
        back={{ to: `${base}/question-banks`, label: 'Question banks' }}
        action={
          editable ? (
            <div className="flex flex-wrap gap-2">
              {aiEnabled && !assist && (
                <AiButton type="button" onClick={() => setAssist(true)}>
                  Draft with AI
                </AiButton>
              )}
              <Link to={`${base}/question-banks/${bankId}/edit`}>
                <Button variant="ghost">Edit details</Button>
              </Link>
              <Link to={`${base}/question-banks/${bankId}/questions/new`}>
                <Button>
                  <Plus className="size-4" aria-hidden /> New question
                </Button>
              </Link>
            </div>
          ) : (
            <Badge>Nirvik library · read-only</Badge>
          )
        }
      />
      {assist && (
        <QuestionAssist
          owner={owner}
          bankId={bankId}
          onSaved={refresh}
          onClose={() => setAssist(false)}
        />
      )}
      <Input
        type="search"
        aria-label="Search questions"
        placeholder="Search questions, answers and explanations"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Card>
        {data.data?.questions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {term ? `No questions match “${term}”.` : 'No questions in this bank yet.'}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {data.data?.questions.map((q) => (
              <li key={q.id} className="flex items-start justify-between gap-3 py-2 text-sm">
                <span>
                  {q.prompt}
                  <span className="block text-xs text-muted-foreground">
                    {skillLabel(q.skill)} · {TYPE_LABELS[q.type]}
                  </span>
                </span>
                {editable && (
                  <span className="flex shrink-0 gap-1">
                    <Link to={`${base}/question-banks/${bankId}/questions/${q.id}`}>
                      <Button variant="ghost">Edit</Button>
                    </Link>
                    <Button
                      variant="ghost"
                      aria-label="Archive question"
                      disabled={archiveQuestion.isPending}
                      onClick={() => archiveQuestion.mutate(q.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
      {editable && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            disabled={archiveBank.isPending}
            onClick={() => archiveBank.mutate()}
          >
            Archive question bank
          </Button>
        </div>
      )}
      <ErrorText error={archiveQuestion.error ?? archiveBank.error} />
    </div>
  );
}

// ─── Question editor (its own page) ────────────────────────────────────

const newOption = (i: number, correct = false): QuestionOption => ({
  id: `o${Date.now().toString(36)}${i}`,
  text: '',
  correct,
});
const TRUE_FALSE: QuestionOption[] = [
  { id: 'true', text: 'True', correct: true },
  { id: 'false', text: 'False', correct: false },
];

export function QuestionEditor({ base }: { base: string }) {
  const { bankId = '', questionId } = useParams();
  const [params] = useSearchParams();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const existing = useQuery({
    queryKey: ['question', questionId],
    queryFn: () => api.question(questionId!),
    enabled: Boolean(questionId),
  });
  const returnTo = params.get('returnTo') ?? `${base}/question-banks/${bankId}`;

  const [form, setForm] = useState<QuestionInput>({
    type: 'single',
    skill: 'phishing',
    difficulty: 2,
    prompt: '',
    explanation: '',
    options: [newOption(0, true), newOption(1)],
  });
  useEffect(() => {
    const q = existing.data;
    if (q)
      setForm({
        type: q.type,
        skill: q.skill,
        difficulty: q.difficulty,
        prompt: q.prompt,
        options: q.options,
        explanation: q.explanation ?? '',
      });
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () => {
      const input = { ...form, explanation: form.explanation || undefined };
      return questionId ? api.updateQuestion(questionId, input) : api.createQuestion(bankId, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank', bankId] });
      queryClient.invalidateQueries({ queryKey: ['banks'] });
      queryClient.invalidateQueries({ queryKey: ['question', questionId] });
      navigate(returnTo);
    },
  });

  const setType = (type: QuestionType) =>
    setForm((f) => ({
      ...f,
      type,
      options:
        type === 'true_false'
          ? TRUE_FALSE
          : f.type === 'true_false'
            ? [newOption(0, true), newOption(1)]
            : f.options,
    }));
  const setOption = (i: number, patch: Partial<QuestionOption>) =>
    setForm((f) => ({
      ...f,
      options: f.options.map((o, j) => {
        if (j === i) return { ...o, ...patch };
        // One correct answer for single and true/false questions.
        return patch.correct && f.type !== 'multiple' ? { ...o, correct: false } : o;
      }),
    }));

  if (questionId && existing.isPending)
    return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="max-w-3xl">
      <PageHeader
        title={questionId ? 'Edit question' : 'New question'}
        back={{ to: returnTo, label: 'Back' }}
        description={
          questionId
            ? 'Changes apply to drafts. Live quizzes keep their published copy until you publish again.'
            : undefined
        }
      />
      <Card>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Type" htmlFor="q-type">
              <Select
                id="q-type"
                value={form.type}
                onChange={(e) => setType(e.target.value as QuestionType)}
              >
                {QUESTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Skill" htmlFor="q-skill">
              {/* Built-in cyber skills are suggested; any short slug (e.g. tool_use) works. */}
              <Input
                id="q-skill"
                list="q-skill-options"
                required
                pattern="[a-z][a-z0-9_]{1,39}"
                title="A short lowercase slug, e.g. phishing or tool_use"
                value={form.skill}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    skill: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                  }))
                }
              />
              <datalist id="q-skill-options">
                {SKILLS.map((s) => (
                  <option key={s} value={s}>
                    {skillLabel(s)}
                  </option>
                ))}
              </datalist>
            </Field>
            <Field label="Difficulty" htmlFor="q-diff">
              <Select
                id="q-diff"
                value={form.difficulty}
                onChange={(e) => setForm((f) => ({ ...f, difficulty: Number(e.target.value) }))}
              >
                <option value={1}>Easy</option>
                <option value={2}>Medium</option>
                <option value={3}>Hard</option>
              </Select>
            </Field>
          </div>
          <Field label="Question" htmlFor="q-prompt">
            <Textarea
              id="q-prompt"
              rows={3}
              required
              minLength={5}
              value={form.prompt}
              onChange={(e) => setForm((f) => ({ ...f, prompt: e.target.value }))}
            />
          </Field>
          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium">
              Answers{' '}
              {form.type === 'multiple' ? '(check every correct one)' : '(select the correct one)'}
            </legend>
            {form.options.map((o, i) => (
              <div key={o.id} className="flex items-center gap-2">
                <input
                  type={form.type === 'multiple' ? 'checkbox' : 'radio'}
                  className="size-5 shrink-0 accent-primary"
                  name="correct"
                  aria-label={`Answer ${i + 1} is correct`}
                  checked={o.correct}
                  onChange={(e) => setOption(i, { correct: e.target.checked })}
                />
                <Input
                  aria-label={`Answer ${i + 1}`}
                  required
                  value={o.text}
                  readOnly={form.type === 'true_false'}
                  onChange={(e) => setOption(i, { text: e.target.value })}
                />
                {form.type !== 'true_false' && form.options.length > 2 && (
                  <button
                    type="button"
                    aria-label="Remove answer"
                    className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-30"
                    onClick={() =>
                      setForm((f) => ({ ...f, options: f.options.filter((_, j) => j !== i) }))
                    }
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>
            ))}
            {form.type !== 'true_false' && form.options.length < 8 && (
              <button
                type="button"
                className="-my-2 py-2 text-sm text-primary"
                onClick={() =>
                  setForm((f) => ({ ...f, options: [...f.options, newOption(f.options.length)] }))
                }
              >
                Add an answer
              </button>
            )}
          </fieldset>
          <Field
            label="Explanation"
            htmlFor="q-explain"
            hint="Shown after answering: why the right answer is right"
          >
            <Textarea
              id="q-explain"
              rows={2}
              value={form.explanation ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, explanation: e.target.value }))}
            />
          </Field>
          <Button type="submit" disabled={save.isPending}>
            {questionId ? 'Save question' : 'Add question'}
          </Button>
          <ErrorText error={save.error} />
        </form>
      </Card>
    </div>
  );
}
